/* Fetches & validates data for a session*/

import { getCookie } from '@jouzen/ecom-utils';

import { type Partner, type PartnerConfig } from '@/configs/partner';
import { logger as browserLogger } from '@/logger/logger.browser';
import { Account } from '@/types';
import { ApiError } from '@/types/Error';

// eslint-disable-next-line no-restricted-imports
import { mocks } from '../../../mocks/apiMocks';
import { checkEligibility } from './checkEligibility';
import { getOrders } from './getOrders';
import { getUnclaimedEligibilities } from './getUnclaimedEligibilities';

interface FetchSessionResponse {
  data?: {
    account?: Account;
  };
  error?: ApiError | unknown;
}

const getMockResponse = (partnerConfig: PartnerConfig) => {
  const MOCK_DATA = getCookie('mock')?.toUpperCase();

  if (!MOCK_DATA) return undefined;

  if (MOCK_DATA === 'INVALID_USER') {
    return {
      error: mocks[MOCK_DATA].getOrders.error,
    };
  }

  const orders = partnerConfig.getOrders
    ? mocks[MOCK_DATA].getOrders
    : undefined;
  const eligibilities = partnerConfig.getEligibilities
    ? mocks[MOCK_DATA].getEligibilities
    : undefined;

  return {
    data: {
      account: {
        ...(orders && { orders: orders.data }),
        ...(eligibilities && { eligibilities: eligibilities.data }),
      },
    },
  };
};

export const fetchSessionData = async (
  email: string,
  emailToken: string | null,
  partner: Partner,
  partnerConfig: PartnerConfig,
): Promise<FetchSessionResponse> => {
  if (partnerConfig.checkEligibility) {
    const response = await checkEligibility(emailToken!, email);
    if (!response?.eligible) return {};
  }

  const mockResponse = getMockResponse(partnerConfig);
  if (mockResponse) return mockResponse;

  try {
    const [ordersResponse, eligibilitiesResponse] = await Promise.allSettled([
      partnerConfig.getOrders
        ? getOrders(email, emailToken, partner)
        : undefined,
      partnerConfig.getEligibilities
        ? getUnclaimedEligibilities(email, partner)
        : undefined,
    ]);

    const ordersError =
      ordersResponse.status === 'rejected'
        ? ordersResponse.reason
        : ordersResponse.value?.error;
    const eligibilitiesError =
      eligibilitiesResponse.status === 'rejected'
        ? eligibilitiesResponse.reason
        : eligibilitiesResponse.value?.error;

    if (ordersError && eligibilitiesError)
      return {
        error: new AggregateError(
          [ordersError, eligibilitiesError],
          'Failed to fetch both orders and eligibilities',
        ),
      };

    const orders =
      ordersResponse.status === 'fulfilled'
        ? ordersResponse.value?.data
        : undefined;
    const eligibilities =
      eligibilitiesResponse.status === 'fulfilled'
        ? eligibilitiesResponse.value?.data
        : undefined;

    return {
      data: {
        account: {
          ...(orders && { orders }),
          ...(eligibilities && { eligibilities }),
        },
      },
    };
  } catch (error) {
    browserLogger.warn('fetchSessionData failed', {
      error,
      email,
      partner,
    });
    return { error };
  }
};
